import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';

import {
  AppointmentsList,
  type AppointmentsQueryState,
} from '../../features/appointments/appointments-list';
import {
  presetRange,
  startOfLocalDay,
  type AppointmentSortKey,
} from '../../features/appointments/filters';
import type { AppointmentStatus } from '../../features/appointments/types';
import { addLocalDays, formatLocalDate } from '../../features/scheduling/availability-preview';
import { useOperationsClient } from '../../lib/api/operations-client-context';
import { useSchedulingClient } from '../../lib/api/scheduling-client-context';

export const Route = createFileRoute('/appointments/')({
  component: AppointmentsPage,
});

const CLINIC_ZONE = 'America/Los_Angeles';

/**
 * The order an **All dates** page is actually served in (M13.7).
 *
 * `sort=startAt` means "soonest first", and *soonest* needs a floor to be measured from.
 * "All dates" clears the range, so the endpoint answers an unbounded ascending query as
 * `-startAt` — otherwise page one is the twenty-five oldest visits the clinic has on file
 * and a booking taken a minute ago is three pages down (`effectiveSort()` in
 * `appointments-list.service.ts`).
 *
 * The screen has to say the same thing. The Date header reads its arrow and its
 * "sorted ascending" announcement straight off `query.sort`, so leaving `startAt` pinned
 * there meant the column told a screen reader one order while the rows arrived in the
 * other — and the one person who most needs the header to be true is the one who cannot
 * see the dates disagree with it. Deriving the value both the request and the header use
 * keeps them one decision instead of two.
 *
 * Only this one combination moves. Every bounded preset stays ascending, and every other
 * sort key — including `createdAt` in either direction, which is meaningful with no
 * range — is asked for exactly as chosen.
 */
export function listSortFor(
  query: Pick<AppointmentsQueryState, 'preset' | 'sort'>,
): AppointmentSortKey {
  return query.preset === 'all' && query.sort === 'startAt' ? '-startAt' : query.sort;
}

/**
 * `/appointments` — the appointment list (MASTER_PLAN M11.2, Docs/API.md §2.5).
 *
 * The route owns the query and the cursor stack. Cursors are opaque and one-directional
 * (API.md §1.7), so "Previous" is a stack of the cursors already used rather than a
 * backwards query — which is also how the server wants it.
 *
 * TODO(M4.12): the clinic's time zone comes from tenant settings; until M4.12 it is read
 * from the scheduling seam's settings, which is the same source the booking screens use.
 */
function AppointmentsPage() {
  const client = useOperationsClient();
  const scheduling = useSchedulingClient();

  const [query, setQuery] = useState<AppointmentsQueryState>(() => ({
    preset: 'next_7',
    fromDate: '',
    toDate: '',
    status: '',
    providerId: '',
    appointmentTypeId: '',
    readiness: 'any',
    q: '',
    sort: 'startAt',
  }));
  const [cursors, setCursors] = useState<(string | null)[]>([null]);

  const settings = useQuery({
    queryKey: ['scheduling-settings'],
    queryFn: () => scheduling.getSettings(),
  });
  const providers = useQuery({
    queryKey: ['providers'],
    queryFn: () => scheduling.listProviders(),
  });
  const types = useQuery({
    queryKey: ['appointment-types'],
    queryFn: () => scheduling.listAppointmentTypes(),
  });

  const timeZone = settings.data?.timeZone ?? CLINIC_ZONE;

  /* The range the endpoint is actually asked for. Recomputed only when the dates change. */
  const range = useMemo(() => {
    if (query.preset !== 'custom') return presetRange(query.preset, new Date(), timeZone);
    if (query.fromDate === '' && query.toDate === '') return { from: null, to: null };
    return {
      from: query.fromDate === '' ? null : startOfLocalDay(query.fromDate, timeZone),
      /* `to` is exclusive on the wire, so an inclusive end date is the next midnight. */
      to: query.toDate === '' ? null : startOfLocalDay(addLocalDays(query.toDate, 1), timeZone),
    };
  }, [query.preset, query.fromDate, query.toDate, timeZone]);

  const cursor = cursors[cursors.length - 1] ?? null;

  /* The order this page is both asked for and labelled with. See `listSortFor`. */
  const sort = listSortFor(query);

  const appointments = useQuery({
    queryKey: [
      'appointments',
      range.from,
      range.to,
      query.status,
      query.providerId,
      query.appointmentTypeId,
      query.readiness,
      query.q,
      sort,
      cursor,
    ],
    queryFn: () =>
      client.listAppointments({
        from: range.from,
        to: range.to,
        ...(query.status === '' ? {} : { status: [query.status as AppointmentStatus] }),
        providerId: query.providerId === '' ? null : query.providerId,
        appointmentTypeId: query.appointmentTypeId === '' ? null : query.appointmentTypeId,
        readiness: query.readiness,
        q: query.q,
        sort,
        limit: 25,
        cursor,
      }),
  });

  return (
    <AppointmentsList
      rows={appointments.data?.data ?? []}
      query={{ ...query, sort }}
      onQueryChange={(next) => {
        /* Any change to the filters starts the paging again: an old cursor is bound to
           the old query and the server rejects it (API.md §1.7). */
        setQuery((current) => ({
          ...next,
          /* The list was rendered with the *derived* sort, so that is what it hands back
             when the staff member changed something else. Storing it would make a trip
             through "All dates" leave every bounded preset descending afterwards, which
             is not what they asked for; keeping `current.sort` unless the header itself
             moved is what makes the derivation invisible everywhere it does not apply. */
          sort: next.sort === listSortFor(current) ? current.sort : next.sort,
          fromDate:
            next.preset === 'custom' && next.fromDate === ''
              ? formatLocalDate(new Date().getTime(), timeZone)
              : next.fromDate,
          toDate:
            next.preset === 'custom' && next.toDate === ''
              ? addLocalDays(formatLocalDate(new Date().getTime(), timeZone), 14)
              : next.toDate,
        }));
        setCursors([null]);
      }}
      providers={(providers.data ?? []).map((provider) => ({
        id: provider.id,
        name: provider.displayName,
      }))}
      appointmentTypes={(types.data ?? []).map((type) => ({ id: type.id, name: type.name }))}
      clinicTimeZone={timeZone}
      isLoading={appointments.isPending}
      error={appointments.isError ? (appointments.error as Error).message : undefined}
      hasNext={appointments.data?.nextCursor != null}
      hasPrevious={cursors.length > 1}
      onNext={() => {
        const next = appointments.data?.nextCursor;
        if (next == null) return;
        setCursors((current) => [...current, next]);
      }}
      onPrevious={() => {
        setCursors((current) => (current.length > 1 ? current.slice(0, -1) : current));
      }}
      renderLink={(to, children, className): ReactNode => (
        <Link to={to} className={className}>
          {children}
        </Link>
      )}
    />
  );
}
